import { Badge, Group, Paper, SegmentedControl, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { useState } from 'react';

import { DataPanel } from '../../components/DataPanel';
import { DetailField, DetailFieldGrid } from '../../components/detail/DetailField';
import { EntityRef } from '../../components/EntityRef';
import type { MonitoringStateResponse } from '../../types/api';

function SummaryCard({ label, value, subtitle }: { label: string; value: string; subtitle?: string }) {
    return (
        <Paper bg="gray.1" p="md" radius="lg">
            <Stack gap={4}>
                <Text tt="uppercase" fw={600} size="xs" c="dimmed" lh={1}>
                    {label}
                </Text>
                <Text fw={600} size="md">
                    {value}
                </Text>
                {subtitle && (
                    <Text size="sm" c="dimmed">
                        {subtitle}
                    </Text>
                )}
            </Stack>
        </Paper>
    );
}

export function MonitoringStateSection({ state }: { state: MonitoringStateResponse }) {
    const { current, previous, changes, timeline } = state;
    const [view, setView] = useState<'current' | 'previous'>('current');
    const activeState = view === 'current' ? current : (previous ?? current);
    const allDimensions = [...new Set([...Object.keys(current), ...Object.keys(previous ?? {})])];

    return (
        <Stack gap="lg">
            {/* Summary cards */}
            <SimpleGrid cols={{ base: 2, sm: 4 }}>
                {current.businessStatus && (
                    <SummaryCard label="Current Status" value={current.businessStatus} />
                )}
                {current.subjectRelationship && (
                    <SummaryCard label="Subject–Place" value={current.subjectRelationship} />
                )}
                {current.place && (
                    <SummaryCard
                        label="Place"
                        value={current.place}
                        subtitle={`${Math.max(1, timeline.length)} verified observations`}
                    />
                )}
                {changes.length > 0 && (
                    <SummaryCard
                        label="Change"
                        value={`${changes.length} change${changes.length > 1 ? 's' : ''} detected`}
                        subtitle={changes.map((c) => c.label).join(', ')}
                    />
                )}
            </SimpleGrid>

            {/* Current State */}
            <DataPanel>
                <Group justify="space-between" align="center" mb="md">
                    <Title order={6}>Current State</Title>
                    {previous && (
                        <SegmentedControl
                            size="xs"
                            value={view}
                            onChange={(v) => setView(v as 'current' | 'previous')}
                            data={[
                                { label: 'Current', value: 'current' },
                                { label: 'Previous', value: 'previous' },
                            ]}
                        />
                    )}
                </Group>
                <Text size="sm" c="dimmed" mb="md">
                    {view === 'current'
                        ? 'Latest known physical-world state from approved observations.'
                        : 'State before the most recent observation was added.'}
                </Text>
                <DetailFieldGrid>
                    {allDimensions.map((dim) => (
                        <DetailField key={dim} label={dim} value={activeState[dim] ?? '—'} />
                    ))}
                </DetailFieldGrid>
                {view === 'current' && changes.length > 0 && (
                    <Paper withBorder p="md" radius="md" mt="md">
                        <Group justify="space-between" mb="xs">
                            <Text size="sm" fw={500}>Change from previous observation</Text>
                            <Badge size="sm" variant="light" color="blue">
                                {changes.length} change{changes.length > 1 ? 's' : ''}
                            </Badge>
                        </Group>
                        <Stack gap="xs">
                            {changes.map((c) => (
                                <Group key={c.dimension} justify="space-between">
                                    <Text size="sm" c="dimmed">{c.label}</Text>
                                    <Group gap="xs">
                                        {c.previous && <Badge size="sm" variant="light" color="gray">{c.previous}</Badge>}
                                        <Text size="sm">→</Text>
                                        {c.current && <Badge size="sm" variant="light" color="green">{c.current}</Badge>}
                                    </Group>
                                </Group>
                            ))}
                        </Stack>
                    </Paper>
                )}
            </DataPanel>

            {/* Timeline */}
            {timeline.length > 0 && (
                <DataPanel>
                    <Title order={6} mb="md">Visits & Observation History</Title>
                    <Stack gap="sm">
                        {[...timeline].reverse().map((entry) => (
                            <Paper key={entry.visitId} withBorder p="md" radius="md">
                                <Group justify="space-between" mb="xs">
                                    <Group gap="xs">
                                        <Text size="sm" fw={500}>
                                            {new Date(entry.submittedAt).toLocaleDateString('en-US', {
                                                month: 'short', day: 'numeric', year: 'numeric',
                                            })}
                                        </Text>
                                        <Text size="sm" c="dimmed">{entry.visitType}</Text>
                                    </Group>
                                    <EntityRef title="View visit" id={entry.visitId} href={`/visits/${entry.visitId}`} />
                                </Group>
                                <Group gap="xs" mb="xs">
                                    <Text size="sm">
                                        {[
                                            entry.cumulative.businessStatus,
                                            entry.cumulative.subjectRelationship,
                                            entry.cumulative.asset,
                                        ].filter(Boolean).join(' · ')}
                                    </Text>
                                    {entry.isBaseline ? (
                                        <Badge size="sm" variant="light" color="gray">Baseline</Badge>
                                    ) : entry.changes.length > 0 ? (
                                        <Badge size="sm" variant="light" color="blue">
                                            {entry.changes.length} change{entry.changes.length > 1 ? 's' : ''}
                                        </Badge>
                                    ) : null}
                                </Group>
                                {entry.changes.length > 0 && (
                                    <Text size="sm" c="dimmed">
                                        {entry.changes.map((c) => {
                                            if (c.previous && c.current) return `${c.label} changed from ${c.previous} to ${c.current}`;
                                            if (c.current) return `${c.label}: ${c.current}`;
                                            return `${c.label}: removed`;
                                        }).join('; ')}
                                    </Text>
                                )}
                            </Paper>
                        ))}
                    </Stack>
                </DataPanel>
            )}
        </Stack>
    );
}
